import React, { useEffect, useState, useCallback } from 'react';
import { VesselProvider, useVessel } from './contexts/VesselContext';
import TailwindShell from './components/tailwind/TailwindShell';
import StorageManager from './components/tailwind/StorageManager';
import { UniversalSyncBridge } from './sync/UniversalSyncBridge';
import notificationService from './services/notificationService';
import fleetEscalationService from './services/fleetEscalationService';
import logger from './utils/logger';
import { useGlobalEquipmentSync } from './hooks/useGlobalEquipmentSync';
import { FLAGS, isOptionBAuthBoundaryEnabled } from './config/featureFlags';
import toast, { Toaster } from 'react-hot-toast';
import { AuthGate } from './components/tailwind/auth/AuthGate';
import { ImpersonationProvider } from './platform/auth/impersonationProvider';
import { ImpersonationBanner } from './components/admin/ImpersonationBanner';
import { IncognitoWarningModal } from './components/platform/IncognitoWarningModal';
// FT-192: PWA update banner - user-triggered updates
import { UpdateBanner } from './components/tailwind/common/UpdateBanner';
// FT-236: Offline status banner - visible notification when network lost
import { OfflineStatusBanner } from './components/tailwind/common/OfflineStatusBanner';
import './styles/tailwind.css';
import './styles/tailwind-hours.css';
import './styles/emergency.css';

// Make FLAGS available globally for maintenance v2
window.FLAGS = FLAGS;

/**
 * FT-365 v3 Layer D: VesselProvider keyed by the last-recorded user id.
 *
 * === FT-365 Option B (2026-07): Layer D is now feature-flag gated. ===
 *
 * Under Option B (flag ENABLED — default), Layer D is REMOVED from the render
 * tree. AuthGate's mismatch branch calls dispatchUserBoundaryReload() from
 * src/lib/authBoundary.js, which fires window.location.reload() — the reload
 * itself is the sole mechanism that tears down user-scoped state (this
 * wrapper is redundant when the reload invariant holds).
 *
 * Under Option B (flag DISABLED — rollback path), this wrapper renders
 * unchanged and behaves exactly as v3.5.1. See feedback_auth_boundary_reload_invariant.md.
 *
 * === Original v3.5.1 behavior (still in effect when flag is OFF) ===
 *
 * The reload path in AuthGate (login success + logout) already tears down
 * VesselProvider by reloading the whole page. Layer D fires only on non-reload
 * transitions — impersonation start/stop today, silent-refresh user swap
 * tomorrow — where React state would otherwise carry the previous user's
 * VesselContext into the fresh subtree.
 *
 * Key source is LS `sr.auth.lastUserId` (NOT `useAuth().user.id`) because
 * VesselProvider wraps DatabaseApp which contains AuthGate — `useAuth()`
 * called at this level would throw. Hoisting AuthGate above VesselProvider is
 * architecturally infeasible per adversarial review, so LS is the pragmatic
 * choice: it's the same marker AuthGate writes/reads for its user-boundary
 * check, and it fires cross-tab via the storage event as a free bonus.
 *
 * Listens to:
 *   - `storage` event on `sr.auth.lastUserId` — cross-tab
 *   - `sr:auth:userChanged` custom event — same-tab, dispatched by AuthGate
 *     after its LS write completes
 */
const LAST_USER_ID_KEY = 'sr.auth.lastUserId';

function readLastUserId() {
  try {
    // eslint-disable-next-line local/no-localstorage-in-react, no-restricted-syntax -- FT-365 v3 Layer D boundary marker
    return localStorage.getItem(LAST_USER_ID_KEY) || 'anonymous';
  } catch (_) {
    return 'anonymous';
  }
}

function VesselProviderKeyedByAuth({ children }) {
  const [userKey, setUserKey] = useState(readLastUserId);

  const refresh = useCallback(() => {
    setUserKey((prev) => {
      const next = readLastUserId();
      return next === prev ? prev : next;
    });
  }, []);

  useEffect(() => {
    const storageHandler = (e) => {
      if (e && e.key === LAST_USER_ID_KEY) {
        setUserKey(e.newValue || 'anonymous');
      }
    };
    const customHandler = () => { refresh(); };
    window.addEventListener('storage', storageHandler);
    window.addEventListener('sr:auth:userChanged', customHandler);
    return () => {
      window.removeEventListener('storage', storageHandler);
      window.removeEventListener('sr:auth:userChanged', customHandler);
    };
  }, [refresh]);

  return (
    <VesselProvider key={userKey}>
      {children}
    </VesselProvider>
  );
}

// Phase 3.5: Load storage verification utilities (dev only)
if (process.env.NODE_ENV === 'development') {
  import('./lib/storageVerification');
  // Phase 3.6: Load migration helpers (dev only)
  import('./lib/migrationHelpers');
}

/**
 * FleetSkipper - Tailwind Version
 * Clean rebuild with Tailwind CSS
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    logger.error('App crashed:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h1>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              Reload App
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Database-aware app wrapper (boot sequence removed - using Dexie instead)
function DatabaseApp() {
  const { currentVesselId } = useVessel();

  // Initialize global equipment register sync
  useGlobalEquipmentSync();

  // Track 1: initialise telemetry singleton (no-op if gate is off).
  // Safe to call unconditionally; internal IS_TRACK1_ON() gates all side effects.
  // Installs boot-level sr:storage:set + sr:sync:hydrate listeners; captures
  // SW/net state; exposes window.__track1.dump() only when gate is on.
  useEffect(() => {
    try {
      // Lazy import so the telemetry module isn't in the boot critical path
      // when gate is off (dead-code eliminated by CRA in prod builds).
      import('./utils/track1Telemetry').then(mod => {
        try { mod.track1.init(); } catch (_) { /* noop */ }
      }).catch(() => { /* noop */ });
    } catch (_) { /* noop */ }
  }, []);

  // FT-344 (2026-07-08): Install cloud → local ingest listener for photos.
  // Boot-level once. Uninstall on unmount. See src/sync/photoPullMerge.js.
  useEffect(() => {
    let uninstall = null;
    import('./sync/photoPullMerge').then((mod) => {
      try { uninstall = mod.installPhotoPullListener(); } catch (_) { /* noop */ }
    }).catch(() => { /* noop */ });
    return () => { try { if (uninstall) uninstall(); } catch (_) { /* noop */ } };
  }, []);

  // Phase 1: Start automatic outbox processing (every 30 seconds)
  useEffect(() => {
    const outboxInterval = setInterval(async () => {
      try {
        const { processOutbox } = await import('./lib/outbox');
        const { azureCloudClient } = await import('./sync/azureCloudClient');

        // Pass cloud client to processOutbox for sync operations
        const result = await processOutbox(azureCloudClient);

        if (result.success > 0 || result.failed > 0) {
          console.log(
            `[Outbox] Processed: ${result.success} synced, ${result.failed} failed, ${result.skipped || 0} skipped`
          );
        }
      } catch (error) {
        console.error('[Outbox] Processing failed:', error);
      }
    }, 30000);  // 30 seconds

    return () => {
      clearInterval(outboxInterval);  // Cleanup interval on unmount
    };
  }, []);

  // Render the main app directly (no boot sequence needed)
  // AuthGate enabled={true} - authentication is now active
  // First-time users will see SetupWizard, returning users see login
  return (
    <AuthGate enabled={true}>
      <ImpersonationProvider>
        {/* Phase 6: Data Safeguards - Incognito Warning (blocking) */}
        <IncognitoWarningModal />

        {/* Phase 6: Admin Impersonation - Warning Banner */}
        <ImpersonationBanner />

        <UniversalSyncBridge enabled={true}>
          <StorageManager />
          <TailwindShell />
        </UniversalSyncBridge>
      </ImpersonationProvider>
    </AuthGate>
  );
}

function TailwindApp() {
  // FT-192: PWA update prompt - show banner when new version available
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);

  // FT-236: Offline status banner - track network state
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // FT-192: Listen for service worker updates
  useEffect(() => {
    // This effect sets up the onUpdate callback to show the banner
    // The actual SW registration is in index.js, but we can listen for
    // a custom event dispatched from there
    const handleSwUpdate = (event) => {
      const { registration } = event.detail || {};
      if (registration && registration.waiting) {
        setWaitingWorker(registration.waiting);
        setShowUpdateBanner(true);
        console.log('[TailwindApp] SW update detected - showing banner');
      }
    };

    window.addEventListener('sw:update', handleSwUpdate);
    return () => window.removeEventListener('sw:update', handleSwUpdate);
  }, []);

  // FT-236: Listen for network online/offline events
  useEffect(() => {
    const handleOffline = () => {
      setIsOffline(true);
      console.log('[TailwindApp] Device went offline');
    };

    const handleOnline = () => {
      setIsOffline(false);
      console.log('[TailwindApp] Device back online');
      // Show brief "Back online" toast
      toast.success('Back online — syncing now.', {
        duration: 3000,
        icon: '✅',
      });
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  // FT-192: Handle update button click
  const handleUpdate = () => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      // The controllerchange listener in serviceWorkerRegistration.js will reload
    }
  };

  // FT-192: Handle dismiss button click
  const handleDismiss = () => {
    setShowUpdateBanner(false);
  };

  useEffect(() => {
    // Auto-run org-scoped migration on first load (Phase 4)
    if (FLAGS.ORG_SCOPED_STORAGE) {
      const migrated = localStorage.getItem('MIGRATION_LOG');
      if (!migrated && window.runOrgScopedMigration) {
        logger.info('[App] Auto-running org-scoped storage migration...');
        try {
          window.runOrgScopedMigration();
        } catch (error) {
          logger.error('[App] Migration failed:', error);
        }
      }
    }

    try {
      notificationService.initialize();
    } catch (error) {
      logger.error('Failed to initialize notifications:', error);
    }

    // Initialize fleet escalation service for shore edit monitoring
    try {
      fleetEscalationService.initialize({
        onLevel1: (notif, vesselId) => {
          logger.info(`[Fleet] Level 1 escalation: ${notif.id} for vessel ${vesselId}`);
        },
        onLevel2: (notif, vesselId) => {
          logger.warn(`[Fleet] Level 2 escalation (DPA): ${notif.id} for vessel ${vesselId}`);
        },
        onLevel3: (notif, vesselId) => {
          logger.error(`[Fleet] Level 3 escalation (COMPLIANCE): ${notif.id} for vessel ${vesselId}`);
        }
      });
    } catch (error) {
      logger.error('Failed to initialize fleet escalation service:', error);
    }

    // Dev-only warning for legacy keys - Anchor Mode v2.0
    if (process.env.NODE_ENV !== 'production') {
      const legacyPatterns = [
        /crewMembers_/,
        /maintenanceHistory_/,
        /hoursOfRest_/,
        /quicklog_hours_of_rest_/i,
        /drillRecords_/,
        /equipmentData_/
      ];
      const bad = [];
      // Using window.localStorage to access storage directly in dev mode only
      // This is for dev-only warning and doesn't affect app behavior
      const storage = window.localStorage;
      for (let i = 0; i < storage.length; i++) {
        const k = storage.key(i);
        if (legacyPatterns.some(rx => rx.test(k))) bad.push(k);
      }
      if (bad.length) {


      }
    }

    return () => {
      try {
        notificationService.destroy();
      } catch (error) {
        logger.error('Failed to destroy notifications:', error);
      }

      try {
        fleetEscalationService.stop();
      } catch (error) {
        logger.error('Failed to stop fleet escalation service:', error);
      }
    };
  }, []);

  // FT-365 Option B: when flag is ENABLED (default), skip the Layer D
  // keyed wrapper — window.location.reload() from dispatchUserBoundaryReload
  // is the sole tear-down mechanism. When flag is DISABLED (rollback path),
  // Layer D renders as in v3.5.1. See feedback_auth_boundary_reload_invariant.md.
  const useLayerD = !isOptionBAuthBoundaryEnabled();
  const VesselProviderWrapped = useLayerD ? VesselProviderKeyedByAuth : VesselProvider;

  return (
    <ErrorBoundary>
      {/* FT-365 v3 Layer D (v3.5.1) → Option B flag-gated (Phase 1):
          - flag ON  (default): renders <VesselProvider> directly, no key= remount race.
          - flag OFF (rollback): renders <VesselProviderKeyedByAuth> as before.
          Either way, VesselProvider provides the vessel context to DatabaseApp. */}
      <VesselProviderWrapped>
        {/* FT-192: Show update banner when new service worker is waiting */}
        {showUpdateBanner && (
          <UpdateBanner
            onUpdate={handleUpdate}
            onDismiss={handleDismiss}
          />
        )}
        {/* FT-236: Show offline banner when network lost */}
        {isOffline && <OfflineStatusBanner />}
        <Toaster position="top-right" />
        <DatabaseApp />
      </VesselProviderWrapped>
    </ErrorBoundary>
  );
}

export default TailwindApp;